import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Calendar, ChevronRight, Sparkles, X, User, Key, ChevronLeft, ChevronDown, ChevronUp, DollarSign, Search, RefreshCw, Shield, Check, Trash2, UserCog } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Profile, Message } from '../lib/supabase-types';
import { toast } from 'sonner';
import SystemPromptEditor from '../components/SystemPromptEditor';

interface UserRow extends Profile {
  loading?: boolean;
  selected?: boolean;
}

interface ChatSession {
  id: string;
  created_at: string;
  messages: Message[];
  isExpanded?: boolean;
}

interface UserMessages extends Message {
  chat_session: {
    id: string;
    created_at: string;
  };
}

const AdminPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectAll, setSelectAll] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentAdminId, setCurrentAdminId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isClearing, setIsClearing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'prompts'>('users');

  // Calculate total revenue
  const calculateTotalRevenue = (users: UserRow[]) => {
    const usdTotal = users
      .filter(user => user.purchase_currency === 'USD')
      .reduce((sum, user) => sum + (user.purchase_amount || 0), 0);
    
    const gbpTotal = users
      .filter(user => user.purchase_currency === 'GBP')
      .reduce((sum, user) => sum + (user.purchase_amount || 0), 0);

    return {
      usd: usdTotal,
      gbp: gbpTotal,
      total: usdTotal + (gbpTotal * 1.25)
    };
  };

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/');
          return;
        }

        setCurrentAdminId(user.id);

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (!profile?.is_admin) {
          navigate('/');
          return;
        }

        const { data: users, error } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        const userRows = users.map(user => ({ ...user, selected: false })) || [];
        setUsers(userRows);
        setFilteredUsers(userRows);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    checkAccess();
  }, [navigate]);

  useEffect(() => {
    const filtered = users.filter(user => 
      user.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const handleAccessToggle = async (userId: string, currentAccess: boolean) => {
    setUsers(prev => prev.map(user =>
      user.id === userId ? { ...user, loading: true } : user
    ));

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ has_lifetime_access: !currentAccess })
        .eq('id', userId);

      if (error) throw error;

      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, has_lifetime_access: !currentAccess, loading: false }
          : user
      ));
      toast.success(`User access ${!currentAccess ? 'granted' : 'removed'} successfully`);
    } catch (err) {
      console.error('Error updating user access:', err);
      setError('Failed to update user access');
      toast.error('Failed to update user access');
      setUsers(prev => prev.map(user =>
        user.id === userId ? { ...user, loading: false } : user
      ));
    }
  };

  const handleAdminToggle = async (userId: string, currentAdminStatus: boolean) => {
    if (userId === currentAdminId) {
      toast.error('You cannot change your own admin status');
      return;
    }

    setUsers(prev => prev.map(user =>
      user.id === userId ? { ...user, loading: true } : user
    ));

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_admin: !currentAdminStatus })
        .eq('id', userId);

      if (error) throw error;

      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, is_admin: !currentAdminStatus, loading: false }
          : user
      ));
      toast.success(`User ${!currentAdminStatus ? 'granted' : 'removed'} admin privileges`);
    } catch (err) {
      console.error('Error updating admin status:', err);
      setError('Failed to update admin status');
      toast.error('Failed to update admin status');
      setUsers(prev => prev.map(user =>
        user.id === userId ? { ...user, loading: false } : user
      ));
    }
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setUsers(prev => prev.map(user => ({
      ...user,
      selected: !selectAll && !user.is_admin && user.id !== currentAdminId
    })));
  };

  const handleSelectUser = (userId: string) => {
    setUsers(prev => prev.map(user => 
      user.id === userId && !user.is_admin && user.id !== currentAdminId
        ? { ...user, selected: !user.selected }
        : user
    ));
    setSelectAll(false);
  };

  const handleBulkDelete = async () => {
    const selectedUsers = users.filter(user => user.selected && !user.is_admin && user.id !== currentAdminId);
    if (selectedUsers.length === 0) return;

    setIsDeleting(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      console.log('Starting bulk delete for', selectedUsers.length, 'users');

      for (const user of selectedUsers) {
        console.log('Deleting user:', user.id);
        
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId: user.id }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
          throw new Error(errorData.error || `Failed to delete user ${user.id}`);
        }

        const result = await response.json();
        console.log('Delete response:', result);
      }

      setUsers(prev => prev.filter(user => !selectedUsers.some(selected => selected.id === user.id)));
      setShowDeleteConfirm(false);
      setSelectAll(false);
    } catch (err) {
      console.error('Error deleting users:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete users. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearCache = async () => {
    try {
      setIsClearing(true);
      localStorage.clear();
      await supabase.auth.refreshSession();
      window.location.reload();
    } catch (err) {
      console.error('Error clearing cache:', err);
      setError('Failed to clear cache. Please try again.');
      setIsClearing(false);
    }
  };

  const handleViewUser = async (user: Profile) => {
    setSelectedUser(user);
    setError(null);
    setIsLoadingChats(true);
    setChatSessions([]);

    try {
      console.log('Fetching chat sessions for user:', user.id);
      
      // First get all chat sessions for this user
      const { data: sessions, error: sessionsError } = await supabase
        .from('chat_sessions')
        .select('id, created_at, messages_count')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (sessionsError) {
        console.error('Error fetching sessions:', sessionsError);
        throw sessionsError;
      }

      console.log('Found sessions:', sessions?.length);

      if (sessions && sessions.length > 0) {
        // Fetch messages for each session
        const messages = await fetchMessagesInBatches(sessions.map(s => s.id));
        console.log('Total messages fetched:', messages.length);

        // Group messages by session
        const groupedSessions = sessions.map(session => ({
          ...session,
          messages: messages.filter(m => m.session_id === session.id) || [],
          isExpanded: false
        }));

        console.log('Grouped sessions:', groupedSessions.length);
        setChatSessions(groupedSessions);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load user data. Please try again.');
    } finally {
      setIsLoadingChats(false);
    }
  };

  const handleToggleSession = (sessionId: string) => {
    setChatSessions(prev => prev.map(session => 
      session.id === sessionId 
        ? { ...session, isExpanded: !session.isExpanded }
        : session
    ));
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;

    setIsResettingPassword(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          newPassword
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset password');
      }

      setShowPasswordModal(false);
      setNewPassword('');
      toast.success('Password reset successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      setError(error instanceof Error ? error.message : 'Failed to reset password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Fetch messages in batches to avoid URL length limits
  const fetchMessagesInBatches = async (sessionIds: string[]): Promise<Message[]> => {
    const batchSize = 10;
    const allMessages: Message[] = [];
    
    for (let i = 0; i < sessionIds.length; i += batchSize) {
      const batchIds = sessionIds.slice(i, i + batchSize);
      try {
        console.log('Fetching messages for batch:', batchIds);
        const { data: messages, error } = await supabase
          .from('messages')
          .select('*')
          .in('session_id', batchIds)
          .order('created_at', { ascending: true });

        if (error) {
          console.error('Error fetching messages batch:', error);
          throw error;
        }
        
        console.log('Received messages:', messages?.length);
        if (messages) allMessages.push(...messages);
      } catch (err) {
        console.error(`Error fetching batch ${i / batchSize + 1}:`, err);
      }
    }

    return allMessages;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-600">Loading...</div>
      </div>
    );
  }

  const revenue = calculateTotalRevenue(users);

  if (selectedUser) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setSelectedUser(null)}
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ChevronLeft className="h-5 w-5 mr-2" />
                  Back to Users
                </button>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Reset Password
                  </button>
                </div>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="px-6 py-4 bg-red-50 border-b border-red-100">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* User Profile */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <User className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedUser.email}</h2>
                  <p className="text-sm text-gray-500">
                    Joined {new Date(selectedUser.created_at).toLocaleDateString()}
                  </p>
                  <div className="mt-2 space-y-2">
                    <p className="text-sm">
                      <span className="text-gray-500">First Name:</span>{' '}
                      <span className="font-medium">{selectedUser.first_name || 'Not set'}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-gray-500">Access Level:</span>{' '}
                      <span className={`font-medium ${selectedUser.has_lifetime_access ? 'text-green-600' : 'text-gray-600'}`}>
                        {selectedUser.has_lifetime_access ? 'Lifetime' : 'Free'}
                      </span>
                    </p>
                    {selectedUser.purchase_amount && (
                      <p className="text-sm">
                        <span className="text-gray-500">Purchase:</span>{' '}
                        <span className="font-medium">
                          {selectedUser.purchase_currency === 'USD' ? '$' : '£'}
                          {selectedUser.purchase_amount.toFixed(2)}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Sessions */}
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Chat History</h3>
              {isLoadingChats ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading chat history...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {chatSessions.map((session) => (
                    <div key={session.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => handleToggleSession(session.id)}
                        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-2" />
                          {new Date(session.created_at).toLocaleString()}
                          <span className="ml-3 px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full text-xs">
                            {session.messages?.length || 0} messages
                          </span>
                        </div>
                        {session.isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      
                      {session.isExpanded && (
                        <div className="p-4 space-y-4">
                          {session.messages.map((message) => (
                            <div
                              key={message.id}
                              className={`p-4 rounded-lg ${
                                message.sender === 'user'
                                  ? 'bg-purple-50 ml-4'
                                  : 'bg-gray-50 mr-4'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-600">
                                  {message.sender === 'user' ? 'User' : 'Echo'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(message.created_at).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-gray-700">{message.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {chatSessions.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No chat sessions found</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Password Reset Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Reset Password for {selectedUser.email}
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                    placeholder="Enter new password"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleResetPassword}
                    disabled={isResettingPassword || !newPassword}
                    className="flex-1 bg-purple-600 text-white rounded-lg py-2 px-4 hover:bg-purple-700 disabled:opacity-50"
                  >
                    {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setNewPassword('');
                    }}
                    className="flex-1 border border-gray-300 rounded-lg py-2 px-4 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Shield className="h-6 w-6 text-purple-600" />
                <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
              </div>
              
              {/* Revenue Summary and Clear Cache */}
              <div className="flex items-center space-x-6">
                <div className="text-sm">
                  <span className="text-gray-500">USD Revenue:</span>
                  <span className="ml-2 font-medium text-gray-900">${revenue.usd.toFixed(2)}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">GBP Revenue:</span>
                  <span className="ml-2 font-medium text-gray-900">£{revenue.gbp.toFixed(2)}</span>
                </div>
                <div className="text-sm font-semibold bg-gradient-to-r from-purple-100 to-pink-100 px-4 py-2 rounded-full">
                  <span className="text-gray-700">Total (USD):</span>
                  <span className="ml-2 text-purple-700">${revenue.total.toFixed(2)}</span>
                </div>
                <button
                  onClick={handleClearCache}
                  disabled={isClearing}
                  className="flex items-center px-4 py-2 text-sm font-medium text-purple-600 hover:text-purple-700 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isClearing ? 'animate-spin' : ''}`} />
                  {isClearing ? 'Clearing...' : 'Clear Cache'}
                </button>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="px-6 py-4 bg-red-50 border-b border-red-100">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'users'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Users
              </button>
              <button
                onClick={() => setActiveTab('prompts')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'prompts'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                System Prompt
              </button>
            </div>
          </div>

          {activeTab === 'users' ? (
            <>
              {/* Search and Bulk Actions */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 space-y-4">
                {/* Search Box */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-sm"
                  />
                </div>

                {/* Bulk Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Select All</span>
                    </label>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={!users.some(user => user.selected)}
                      className="flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected
                    </button>
                  </div>
                  <div className="text-sm text-gray-500">
                    {users.filter(user => user.selected).length} users selected
                  </div>
                </div>
              </div>

              {/* User list */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-12 px-6 py-3">
                        <span className="sr-only">Select</span>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Purchase
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Access Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className={user.selected ? 'bg-purple-50' : undefined}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={user.selected}
                            onChange={() => handleSelectUser(user.id)}
                            disabled={user.is_admin || user.id === currentAdminId}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="flex items-center text-left hover:text-purple-600"
                          >
                            <MessageCircle className="h-4 w-4 mr-2" />
                            <span className="text-sm font-medium">{user.email}</span>
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.purchase_amount ? (
                            <div className="text-sm text-gray-900">
                              {user.purchase_currency === 'USD' ? '$' : '£'}
                              {user.purchase_amount.toFixed(2)}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.is_admin ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                              Admin
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              User
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.has_lifetime_access
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.has_lifetime_access ? 'Lifetime' : 'Free'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.id === currentAdminId ? (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                              Current User
                            </span>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleAdminToggle(user.id, user.is_admin)}
                                disabled={user.loading}
                                className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${
                                  user.is_admin
                                    ? 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                } transition-colors disabled:opacity-50`}
                                title={user.is_admin ? 'Remove Admin' : 'Make Admin'}
                              >
                                {user.loading ? (
                                  <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                                ) : (
                                  <UserCog className="h-3 w-3" />
                                )}
                              </button>
                              <button
                                onClick={() => handleAccessToggle(user.id, user.has_lifetime_access)}
                                disabled={user.loading}
                                className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${
                                  user.has_lifetime_access
                                    ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                                } transition-colors disabled:opacity-50`}
                                title={user.has_lifetime_access ? 'Remove Access' : 'Grant Access'}
                              >
                                {user.loading ? (
                                  <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                                ) : user.has_lifetime_access ? (
                                  <X className="h-3 w-3" />
                                ) : (
                                  <Check className="h-3 w-3" />
                                )}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="p-6">
              <SystemPromptEditor />
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Selected Users
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {users.filter(user => user.selected).length} selected users? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete Users'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;